/**
 * Composite triage badge count — kept in lockstep with the triage feed by
 * counting the same card kinds the feed renders: needs-review threads,
 * needs-attention files (missing a required association), orphaned pending
 * attachments (whose thread has left the feed), and pending Tag promotion
 * proposals. Reuses the existing query
 * wrappers so the cache dedupes with the `/triage` page — no extra fetch when
 * already on triage.
 */

import {useMemo} from 'react';
import {useNeedsAttentionFiles} from '@/matic/features/files/hooks/useNeedsAttentionFiles';
import {useTriageThreads} from './useTriageThreads';
import {useRequiredAssociationTypes} from './useRequiredAssociationTypes';
import {useAllPendingAttachments} from './useAllPendingAttachments';
import {useTagCandidates} from './useTagCandidates';
import {getOrphanedAttachments} from '../utils/thread-buckets';

export function useTriageCount(): number {
  const {data: triageData} = useTriageThreads();
  const {data: requiredTypes} = useRequiredAssociationTypes();
  const requiredTypeIds = useMemo(
    () => (requiredTypes ?? []).map((rt) => rt.objectTypeId),
    [requiredTypes]
  );
  const {data: needsAttentionFiles} = useNeedsAttentionFiles(requiredTypeIds);
  const {data: pendingAttachments} = useAllPendingAttachments();
  const {data: tagCandidates} = useTagCandidates();

  return useMemo(() => {
    const threads = triageData?.items ?? [];
    // Count only threads still awaiting review — not the raw list length. A
    // thread that just auto-confirmed (manual add satisfying the gate) or was
    // confirmed directly is patched in place with `needsReview: false` and kept
    // visible in the feed until refresh; the badge should drop it immediately.
    // Removing the association un-confirms it (`needsReview: true` again), which
    // re-increments here. Orphaned-attachment detection still uses the full
    // thread set so a confirmed-but-present thread keeps its attachments grouped.
    const unreviewedThreadCount = threads.filter((t) => t.needsReview).length;
    const orphaned = getOrphanedAttachments(threads, pendingAttachments ?? []);
    return (
      unreviewedThreadCount +
      (needsAttentionFiles?.items.length ?? 0) +
      orphaned.length +
      (tagCandidates?.length ?? 0)
    );
  }, [triageData, needsAttentionFiles, pendingAttachments, tagCandidates]);
}
